import React, { useCallback } from 'react';
import classNames from 'classnames';
import { get, isFunction } from 'lodash';
import { Button } from '@mui/material';
import { IconArrowLeft, IconRotate } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getFloDetails,
	setTrimFirstPlay,
	skipCta,
	trackCompletionAction,
	trackCtaAction,
} from '../../../Pages/Flo/Flo.reducers';
import qs from 'qs';
import { dialogSelector } from '../../VideoDialog/VideoDialog';
import { hideDialogAction } from '../../VideoDialog/VideoDialog.reducer';
import {
	generateFloLink,
	getIsEmbed,
	sanitizeHtmlLinks,
} from '../../../Common/Common.utils';
import { setAutoPlay } from '../../../Pages/Flo/Flo.actions';
import styles from './AdventureCtaBody.module.css';
import { floSpaceIdSelector } from '../../../Pages/Flo/Flo.selectors';

const validProtocols = ['http', 'https', 'ftp', 'ssh', 'telnet', 'sftp', 'file', 'data'];
const pattern = new RegExp('^(' + validProtocols.join('|') + ')://');

export type AdventureValuesType = {
	subheading: string;
	button: {
		icon?: any;
		name: string;
		url: string;
		floId?: string;
		id?: string;
		type: string;
	}[];
	heading: string;
};

export const AdventureCtaCancelButton = () => (
	<div className={styles.adventureCtaSkipText}>
		Skip and continue
		<IconArrowLeft size={20} className={styles.adventureCtaSkipIcon} />
	</div>
);

const AdventureCtaBody = ({
	values,
	id,
	floEmbedUrls,
	isLastElement,
	floId,
	floIndex,
}: {
	values: AdventureValuesType;
	id?: string;
	floEmbedUrls?: { [key: string]: string };
	isLastElement?: boolean;
	floId: string;
	floIndex: number;
}) => {
	const dispatch = useDispatch();
	const queryObj = qs.parse(location.search.replace('?', ''));
	const isEmbed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["is-embed"]`, false)
	);
	const queryString = qs.stringify({
		...queryObj,
		'hide-header': isEmbed ? true : queryObj['hide-header'] || false,
	});
	const {
		onCancel,
		onConfirm,
		id: dialogId,
	} = useSelector(dialogSelector(floIndex)) || {};

	const onSkipAndContinue = useCallback(() => {
		let keepDialog = false;
		if (isFunction(onCancel)) {
			keepDialog = onCancel(undefined, true);
		}
		if (!keepDialog) {
			dispatch(
				hideDialogAction({
					data: '',
					floId,
					floIndex,
				})
			);
		}
	}, [onCancel, floId, floIndex]);

	const onExternalLinkClick = useCallback(() => {
		// dispatch(hideDialogAction({
		// 				data: dialogId, floId, floIndex
		// 			}));
		if (dialogId === 'start') {
			dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
			let keepDialog = false;
			// if (isFunction(onCancel)) {
			// 	keepDialog = onCancel(undefined, true);
			// }
			// if (!keepDialog) {
			dispatch(
				hideDialogAction({
					data: dialogId,
					floId,
					floIndex,
				})
			);
			// }
		} else if (dialogId === 'end') {
			dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
			let keepDialog = false;
			if (isFunction(onCancel)) {
				keepDialog = onCancel(undefined, true);
			}
			if (!keepDialog) {
				dispatch(
					hideDialogAction({
						data: dialogId,
						floId,
						floIndex,
					})
				);
			}
		} else {
			dispatch(
				hideDialogAction({
					data: dialogId,
					floId,
					floIndex,
				})
			);
			dispatch(skipCta({ id, floId, floIndex }));
		}
	}, [id, dialogId, floId]);

	const externalAppInfo = useSelector((state) =>
		get(state, 'routerContainer.externalAppInfo.token', '')
	);

	const floSpaceId = useSelector(floSpaceIdSelector(floIndex));

	const embeddedElements = document.querySelectorAll('floik-flo');

	const replaceFlo = useCallback(
		(buttonItem) => {
			const element = embeddedElements[floIndex];
			element.setAttribute('flo-id', get(buttonItem, 'floId'));
			dispatch(
				hideDialogAction({
					data: '',
					floId,
					floIndex,
				})
			);
			dispatch(
				getFloDetails({
					customToken: get(externalAppInfo, 'token'),
					isPreview: get(externalAppInfo, 'isPreview'),
					floId: get(buttonItem, 'floId'),
					floIndex: floIndex,
					floSpaceId,
				})
			);
		},
		[externalAppInfo, floIndex, embeddedElements]
	);

	const descriptionPresent = get(values, 'subheading');
	const titlePresent = get(values, 'heading');

	return (
		<div className={styles.wrapper}>
			{descriptionPresent && (
				<div
					className={classNames(styles.description, styles.dialogMessage)}
					dangerouslySetInnerHTML={{
						__html: sanitizeHtmlLinks(get(values, 'subheading')),
					}}
				/>
			)}
			<div className={styles.buttonsListWrapper}>
				<ul className={styles.buttonsList}>
					{get(values, 'button', []).map((buttonItem, index) => {
						let defaultUrl = buttonItem.url;
						const openInNewTab = get(buttonItem, 'openInNewTab') ?? true;
						const buttonType = buttonItem.type || 'skip-n-continue';
						let useAnchor = buttonType !== 'skip-n-continue';
						const internalLink = buttonType === 'publish-link';
						let target = '_blank';

						const openExternalLinkInSameTab =
							buttonType === 'target-link' && !openInNewTab;

						if (internalLink) {
							target = '_self';
						} else if (openExternalLinkInSameTab) {
							target = '_parent';
						}

						if (internalLink) {
							defaultUrl = get(buttonItem, 'publishedUrl') || defaultUrl;
						}
						const embedUrl = get(floEmbedUrls, buttonItem.floId || '', '');
						const url = isEmbed && internalLink ? embedUrl : defaultUrl;
						let link = url || defaultUrl || embedUrl;
						if (link === '#') {
							link = '';
						}
						if (link && !pattern.test(link)) {
							link = `//${link}`;
						}
						const ignoreLink = internalLink && embeddedElements.length > 0;
						link = buttonType !== 'skip-n-continue' && !ignoreLink ? url : '';
						useAnchor = !!link && buttonType !== 'skip-n-continue' && !ignoreLink;

						const isUIRestartButton = get(buttonItem, 'id') === 'ui_restart_button';

						return (
							<li
								key={`${buttonItem.name}_${get(buttonItem, 'id', '')}`}
								className={classNames(styles.buttonsListItem, {
									[styles.noTitleDesc]:
										index === get(values, 'button', []).length - 1 &&
										!descriptionPresent &&
										!titlePresent,
								})}
							>
								<Button
									component={useAnchor ? 'a' : 'button'}
									href={link}
									target={target}
									disableFocusRipple
									disableElevation
									data-is-cta-button="true"
									variant="contained"
									className={classNames(styles.addCtaButton, {
										[styles.outlineButton]: isUIRestartButton,
									})}
									// autoFocus
									onClick={() => {
										if (ignoreLink) {
											replaceFlo(buttonItem);
										} else if (!link || buttonItem.type === 'skip-n-continue') {
											onSkipAndContinue();
										} else if (!internalLink) {
											console.log('external link clicked');
											onExternalLinkClick();
										}
										if (id) {
											dispatch(
												trackCtaAction({
													id,
													skip: buttonItem.type === 'skip-n-continue',
													buttonProperties: [
														{
															key: 'buttonName',
															id: buttonItem.id || buttonItem.name,
														},
													],
													floId,
													floIndex,
												})
											);
										}
										if (isLastElement) {
											dispatch(
												trackCompletionAction({
													viewedPercentage: 100,
													floId,
													floIndex,
												})
											);
										}
									}}
								>
									<div className={styles.inputContainer} title={buttonItem.name}>
										{isUIRestartButton && (
											<IconRotate className={styles.restartIcon} size={16} />
										)}
										{buttonItem.name}
									</div>
									{/*<Button disableElevation className={styles.arrowButton}>*/}
									{/*	<IconArrowLeft className={classNames(styles.arrowIcon)} size={20} />*/}
									{/*</Button>*/}
								</Button>
							</li>
						);
					})}
				</ul>
			</div>
		</div>
	);
};

export default React.memo(AdventureCtaBody);
